import React, { useEffect, useRef } from 'react';
import { gsap, Power1, Power4 } from 'gsap';
import { useTranslation } from 'react-i18next';
import Config from 'theme/config';
import { dreamteamUtilities } from 'utilities/dreamteamUtilities';
import wheelLogo from 'theme/assets/images/wheel-logo.png';

import './BonusWheelView.scss';

export interface IWedgeObject {
  value: number;
  bgColor: string;
  textColor: string;
}

export interface IBonusWheelViewData {
  prizePosition: number; // Not zero indexed
  prizeArray: IWedgeObject[];
  prizeOrderArray: number[];
  showBonus: boolean;
  prizeMultiplier: number;
  prizeAmount: number;
  onFinishedCallback: () => void;
  bonusText: any;
  currencySymbol: string;
  prizeCurrencyCode: string;
}

export interface IBonusWheelViewProps {
  data: IBonusWheelViewData;
}

const BonusWheelView = (props: IBonusWheelViewProps) => {
  const elements = useRef<any>({});
  const { t } = useTranslation();

  // TIMING VARIABLES
  let zoomInOutSpeed = 1;
  let countdownLength = 3;
  let numberOfSpinsBeforeResult = 3;
  let spinsBeforeResultSpeed = 12;
  let delayBeforeShowResult = 0;

  // Wedge variables
  let i = 0;
  let wedgeCounter = 0;
  let radius = 230;
  let totalWedges = 0;
  let wedgePercent = 0;
  let wedgeDegrees = 0;
  let currentRotation = 0;
  let lastRotation = 0;
  let tolerance = 0;
  let outcomeWedge = 0;

  // State
  let valuePositions: any[] = [];

  // TimeLines
  let animateInTL: any;
  let wheelTL: any;
  let tickerTL: any;
  let resultTL: any;
  let showResultTL: any;
  let showResultNoBonusTL: any;

  const getValuePositions = () => {
    props.data.prizeArray.forEach(function (wedgeProp, i) {
      valuePositions[i] = [];
      props.data.prizeOrderArray.forEach(function (wedge, j) {
        if (wedge - 1 === i) {
          valuePositions[i].push(j);
        }
      });
    });
  };

  const drawWheel = () => {
    getValuePositions();
    i = 0;
    wedgeCounter = 0;
    radius = 230;
    totalWedges = props.data.prizeOrderArray.length;
    wedgePercent = 1 / totalWedges;
    wedgeDegrees = 360 / totalWedges;

    function getCoordinatesForPercent(percent: number) {
      const x = radius * Math.cos(2 * Math.PI * percent);
      const y = radius * Math.sin(2 * Math.PI * percent);
      return [x, y];
    }

    // Draw wedges

    const wedgePathGroup = document.createElementNS('http://www.w3.org/2000/svg', 'g');

    for (i = 0; i < totalWedges; i++) {
      const [startX, startY] = getCoordinatesForPercent(0);
      const [endX, endY] = getCoordinatesForPercent(wedgePercent);
      const largeArcFlag = wedgePercent > 0.5 ? 1 : 0;
      const pathData = [
        `M ${startX} ${startY}`, // Move
        `A ${radius} ${radius} 0 ${largeArcFlag} 1 ${endX} ${endY}`, // Arc
        `L 0 0` // Line
      ].join(' ');

      const pathEl = document.createElementNS('http://www.w3.org/2000/svg', 'path');
      pathEl.setAttribute('d', pathData);

      // Set wedge colors

      pathEl.setAttribute('fill', props.data.prizeArray[props.data.prizeOrderArray[wedgeCounter] - 1].bgColor);

      pathEl.setAttribute('transform', 'rotate(' + (360 / totalWedges) * wedgeCounter + ')');

      wedgePathGroup.appendChild(pathEl);
      wedgePathGroup.setAttribute('transform', 'rotate(-90)');

      wedgeCounter++;
    }

    elements.current.wheelEl?.appendChild(wedgePathGroup);

    wedgeCounter = 0;

    const textGroup = document.createElementNS('http://www.w3.org/2000/svg', 'g');

    for (i = 0; i < totalWedges; i++) {
      const textWrap = document.createElementNS('http://www.w3.org/2000/svg', 'g');
      const textEl = document.createElementNS('http://www.w3.org/2000/svg', 'text');

      if (Config.bonusWheel.rotatePrizeText) {
        textWrap.setAttribute('transform', 'rotate(90)');
        textEl.setAttribute('text-anchor', 'start');
        textEl.setAttribute('x', '-42%');
      } else {
        textEl.setAttribute('text-anchor', 'middle');
        textEl.setAttribute('y', '-39%');
      }

      textEl.setAttribute('font-size', '18');
      textEl.setAttribute('alignment-baseline', 'central');
      textEl.setAttribute('font-family', 'Montserrat');
      textEl.setAttribute('transform', 'rotate(' + (360 / totalWedges) * wedgeCounter + ')');

      //Set the text content
      textEl.textContent = dreamteamUtilities.formatCurrencyString(props.data.currencySymbol, props.data.prizeCurrencyCode, props.data.prizeArray[props.data.prizeOrderArray[wedgeCounter] - 1].value);

      textEl.setAttribute('fill', props.data.prizeArray[props.data.prizeOrderArray[wedgeCounter] - 1].textColor);

      textWrap.appendChild(textEl);
      textGroup.appendChild(textWrap);

      wedgeCounter++;
    }

    textGroup.setAttribute('transform', 'rotate(' + 360 / totalWedges / 2 + ')');

    elements.current.wheelEl?.appendChild(textGroup);

    //Draw the Ring Shadow

    const ringShadow = document.createElementNS('http://www.w3.org/2000/svg', 'circle');
    ringShadow.setAttribute('r', `${radius - 5}`);
    ringShadow.setAttribute('fill', 'none');
    ringShadow.setAttribute('stroke-width', '2%');
    ringShadow.setAttribute('stroke', 'rgba(0,0,0,0.2)');

    elements.current.wheelEl?.appendChild(ringShadow);

    //Draw the Outer Ring

    const outerRing = document.createElementNS('http://www.w3.org/2000/svg', 'circle');
    outerRing.setAttribute('r', `${radius}`);
    outerRing.setAttribute('fill', 'none');
    outerRing.setAttribute('stroke-width', '2%');
    outerRing.setAttribute('stroke', Config.bonusWheel.spokesColor);

    elements.current.wheelEl?.appendChild(outerRing);

    //Draw the Inner Circle

    const innerRing = document.createElementNS('http://www.w3.org/2000/svg', 'circle');
    innerRing.setAttribute('r', `${radius / 2.5}`);
    innerRing.setAttribute('fill', Config.bonusWheel.spokesColor);

    elements.current.wheelEl?.appendChild(innerRing);

    //Create the Sticks

    wedgeCounter = 0;

    const circlePathGroup = document.createElementNS('http://www.w3.org/2000/svg', 'g');

    for (i = 0; i < totalWedges; i++) {
      const circEl = document.createElementNS('http://www.w3.org/2000/svg', 'circle');

      circEl.setAttribute('cx', `${radius + 5}`);
      circEl.setAttribute('cy', '0');
      circEl.setAttribute('r', '5');
      circEl.setAttribute('fill', Config.bonusWheel.spokesColor);
      circEl.setAttribute('transform', 'rotate(' + (360 / totalWedges) * wedgeCounter + ')');

      circlePathGroup.appendChild(circEl);

      wedgeCounter++;
    }

    circlePathGroup.setAttribute('transform', 'rotate( -90)');

    elements.current.wheelEl?.appendChild(circlePathGroup);

    // Spin whole wheel into start position

    elements.current.wheelEl?.setAttribute('transform', 'rotate(' + 360 / totalWedges / -2 + ')');
  };

  const animateComponentIn = () => {
    gsap.set(elements.current.tickerEl, {
      opacity: 0
    });

    animateInTL.fromTo(elements.current.innerEl, 2, { opacity: 0 }, { opacity: 1 }).fromTo(
      elements.current.wheelSvgEl,
      {
        width: '0%',
        transformOrigin: 'center center'
      },
      {
        duration: zoomInOutSpeed,
        ease: 'power1.inOut',
        translateY: '12%',
        width: '200%',
        onComplete: animateCountDown
      }
    );
  };

  const animateWheelSpin = () => {
    wheelTL.clear();
    wheelTL
      .set(elements.current.wheelEl, {
        overwrite: true,
        transformOrigin: '50% 50%',
        rotation: 0 - wedgeDegrees / 2
      })
      .to(elements.current.tickerEl, { opacity: 1 })
      .to(elements.current.wheelEl, {
        duration: spinsBeforeResultSpeed,
        rotation: 360 * numberOfSpinsBeforeResult - outcomeWedge * wedgeDegrees - wedgeDegrees / 2,
        transformOrigin: '50% 50%',
        ease: 'power4.out',
        onUpdate: function () {
          // @ts-ignore
          currentRotation = Math.round(gsap.getProperty(elements.current.wheelEl, 'rotation'));
          tolerance = currentRotation - lastRotation;
          if (Math.round(currentRotation + wedgeDegrees) % wedgeDegrees <= tolerance) {
            if (tickerTL.progress() > 0.2 || tickerTL.progress() === 0) {
              tickerTL.play(0);
            }
          }
          lastRotation = currentRotation;
        },
        onComplete: function () {
          resultTL.restart();
        }
      });

    wheelTL.seek(0).play();
  };

  const resetWheel = () => {
    gsap.to(elements.current.tickerEl, {
      opacity: 0,
      duration: 0.35
    });
    animateInTL.reverse();
  };

  const animateCountDown = () => {
    let cdCounter = countdownLength;
    elements.current.countdownNumEl.innerHTML = cdCounter;

    var countdownTL = gsap.timeline({
      repeat: countdownLength - 1,
      delay: 0.5
    });

    countdownTL
      .set(elements.current.countdownEl, {
        scale: 2,
        opacity: 1,
        overwrite: true
      })
      .to(elements.current.countdownEl, {
        duration: 0.25,
        scale: 1,
        ease: 'Power1.easeOut'
      })
      .to(elements.current.countdownEl, {
        duration: 0.25,
        delay: 0.5,
        scale: 2,
        opacity: 0,
        ease: 'Power1.easeOut',
        onComplete: () => {
          cdCounter--;

          if (cdCounter === 0) {
            animateWheelSpin();
            cdCounter = countdownLength;
          }
          elements.current.countdownNumEl.innerHTML = cdCounter;
        }
      });
  };

  const registerTimeLines = () => {
    animateInTL = gsap.timeline({ onReverseComplete: props.data.onFinishedCallback });

    wheelTL = gsap.timeline({ paused: true });

    tickerTL = gsap.timeline({ paused: true });
    tickerTL
      .to(elements.current.tickerEl, {
        duration: 0.13,
        rotation: -20,
        transformOrigin: '50% 20%',
        ease: Power1.easeOut,
        overwrite: true
      })
      .to(elements.current.tickerEl, { duration: 0.13, rotation: 0, ease: Power4.easeOut });

    showResultTL = gsap.timeline({
      paused: true
    });

    showResultTL
      .set(elements.current.bonusEl, {
        scale: 0
      })
      .to(elements.current.bonusEl, {
        delay: delayBeforeShowResult,
        duration: 0.55,
        opacity: 1,
        scale: 1,
        ease: Power1.easeOut,
        onComplete: function () {
          gsap.to(elements.current.bonusEl, {
            delay: 2,
            duration: 0.55,
            opacity: 0,
            scale: 0,
            onComplete: function () {
              gsap.set(elements.current.resultEl, {
                scale: 0
              });

              gsap.to(elements.current.resultEl, {
                duration: 0.55,
                opacity: 1,
                scale: 1,
                onComplete: function () {
                  gsap.to(elements.current.resultEl, {
                    delay: 4,
                    duration: 0.55,
                    opacity: 0,
                    scale: 0,
                    onComplete: function () {
                      resetWheel();
                    }
                  });
                }
              });
            }
          });
        }
      });

    showResultNoBonusTL = gsap.timeline({
      paused: true
    });

    showResultNoBonusTL
      .set(elements.current.resultEl, {
        scale: 0
      })
      .to(elements.current.resultEl, {
        delay: delayBeforeShowResult,
        duration: 0.55,
        opacity: 1,
        scale: 1,
        onComplete: function () {
          gsap.to(elements.current.resultEl, {
            delay: 4,
            duration: 0.55,
            opacity: 0,
            scale: 0,
            onComplete: function () {
              resetWheel();
            }
          });
        }
      });

    resultTL = props.data.showBonus ? showResultTL : showResultNoBonusTL;
  };

  const registerElements = () => {
    elements.current = {
      wheelSvgEl: document.querySelector('.bonus-wheel__wheel-container__svg') as HTMLElement,
      tickerEl: document.querySelector('#indicator') as HTMLElement,
      countdownEl: document.querySelector('.bonus-wheel__countdown') as HTMLElement,
      countdownNumEl: document.querySelector('.bonus-wheel__countdown-num') as HTMLElement,
      wheelEl: document.querySelector('#wheel') as HTMLElement,
      innerEl: document.querySelector('.bonus-wheel__inner') as HTMLElement,
      bonusEl: document.querySelector('.bonus-wheel__bonus') as HTMLElement,
      resultEl: document.querySelector('.bonus-wheel__result') as HTMLElement
    };
  };

  const calculateOutcomePosition = () => {
    const randomPosition = valuePositions[props.data.prizePosition - 1][Math.floor(Math.random() * valuePositions[props.data.prizePosition - 1].length)];
    outcomeWedge = randomPosition;
  };

  useEffect(() => {
    registerElements();
    drawWheel();
    registerTimeLines();
    calculateOutcomePosition();
    animateComponentIn();

    // eslint-disable-next-line
  }, []);

  const injectSvg = () => {
    const useTag = `
      <svg viewBox="-250 -270 510 510"  style="margin:30% -25% 0 -25%;" width="100%">
        <defs>
          <pattern id="logoImage" height="100%" width="100%" patternContentUnits="objectBoundingBox">
          <image class="wheelLogo" xlink:href="${wheelLogo.replace(/^\/+/g, '')}" width="1" height="1" />
          </pattern>
        </defs>
        <g id="wheel-wrapper">
          <g id="wheel"></g>
          <circle cx="0" cy="0" r="70" fill="url(#logoImage)" />
        </g>

        <svg width="23" height="64" viewBox="0 0 23 64" x="-11.5" y="-265">
          <g id="indicator">
            <path d="M-5.36055e-06 23.63C-6.18463e-06 14.2036 5.14872 9.13001 11.5 9.13001C17.8513 9.13001 23 14.2036 23 23.63C23 36.0564 11.5 63.13 11.5 63.13C11.5 63.13 -4.2742e-06 36.0564 -5.36055e-06 23.63Z" fill="black" fill-opacity="0.2" />
            <path d="M-5.36055e-06 14.63C-6.18463e-06 5.2036 5.14872 0.130006 11.5 0.130006C17.8513 0.130005 23 5.2036 23 14.63C23 27.0564 11.5 54.13 11.5 54.13C11.5 54.13 -4.2742e-06 27.0564 -5.36055e-06 14.63Z" fill="${
              Config.bonusWheel.tickerColor
            }" />
            <circle cx="11.5" cy="11.63" r="6.5" transform="rotate(180 11.5 11.63)" fill="#fff" />
          </g>
        </svg>
      </svg>
    `;
    return <div dangerouslySetInnerHTML={{ __html: useTag }} />;
  };

  return (
    <div className="bonus-wheel">
      <div className="bonus-wheel__inner">
        <div className="bonus-wheel__wheel-container ">
          <div className="bonus-wheel__wheel-container__svg">{injectSvg()}</div>
        </div>
        <div className="bonus-wheel__countdown">
          <div className="bonus-wheel__countdown-num"></div>
        </div>
        <div className="bonus-wheel__bonus">
          <div className="bonus-wheel__bonus-text">
            {props.data.prizeMultiplier}x<br />
            {t('x-bonus')}
          </div>
        </div>
        <div className="bonus-wheel__result">
          <div className="bonus-wheel__result-text">{props.data.bonusText}</div>
        </div>
      </div>
    </div>
  );
};

export default BonusWheelView;
